import { useMemo } from "react";
import type { Edge as VisEdge, Node as VisNode } from "vis-network/standalone";
import { shiftHexColor, useGraphTheme } from "@/features/graph/hooks/useGraphTheme";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";

/**
 * Pre-computes per-node display colours using the same radius-normalised shift
 * logic as KnowledgeGraphCanvas (useKnowledgeGraphCanvasView).
 */
export function computeNodeColors(nodes: VisualNode[], graphThemeAccent: string, theme: string): Map<string, string> {
	const colors = new Map<string, string>();
	if (nodes.length === 0) return colors;

	const radii = nodes.map((n) => n.radius ?? 12);
	const minRadius = Math.min(...radii);
	const maxRadius = Math.max(...radii);
	const spread = maxRadius - minRadius;

	for (const node of nodes) {
		const baseColor = node.color || graphThemeAccent;
		const normalized = spread > 0 ? ((node.radius ?? 12) - minRadius) / spread : 0.5;
		const shiftAmount = theme === "dark" ? -0.42 + normalized * 0.7 : -0.21 + normalized * 0.49;
		colors.set(node.id, shiftHexColor(baseColor, shiftAmount));
	}

	return colors;
}

/**
 * Returns vis-network node/edge builder functions and standard network options,
 * matching the styling used in KnowledgeGraphCanvas.
 */
export function useVisNetworkBuilders() {
	const { theme, graphTheme } = useGraphTheme();

	const buildVisNode = useMemo(
		() =>
			(
				node: VisualNode,
				opts: {
					/** Canvas position to seed physics (tiny jitter for explosion). */
					pos?: { x: number; y: number };
					/** Pre-computed normalised radius (0–1) for the colour shift. */
					normalizedRadius?: number;
					/** Override vis-network id (e.g. namespaced id). */
					idOverride?: string;
					/** Override node size. Defaults to node.radius*2 or 24. */
					size?: number;
				},
			): VisNode => {
				const { pos, normalizedRadius = 0.5, idOverride, size } = opts;
				const isDark = theme === "dark";
				const shiftAmount = isDark ? -0.42 + normalizedRadius * 0.7 : -0.21 + normalizedRadius * 0.49;
				const baseColor = node.color || graphTheme.accent;
				const nodeTone = shiftHexColor(baseColor, shiftAmount);

				return {
					id: idOverride ?? node.id,
					label: node.name,
					shape: "dot",
					size: size ?? (node.radius ? node.radius * 2 : 24),
					...(pos !== undefined && { x: pos.x, y: pos.y }),
					color: {
						background: nodeTone,
						border: graphTheme.borderStrong,
						highlight: { background: graphTheme.accentSoft, border: graphTheme.accent },
						hover: { background: shiftHexColor(nodeTone, 0.08), border: graphTheme.accent },
					},
					font: {
						size: 13,
						face: "Inter, system-ui, -apple-system, sans-serif",
						color: graphTheme.text,
						strokeWidth: 2,
						strokeColor: graphTheme.surface,
						align: "center" as const,
					},
					borderWidth: 2.5,
					borderWidthSelected: 4,
					shadow: {
						enabled: true,
						color: isDark ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.15)",
						size: 8,
						x: 2,
						y: 2,
					},
					margin: { top: 10, right: 10, bottom: 10, left: 10 },
				};
			},
		[theme, graphTheme],
	);

	const buildVisEdge = useMemo(
		() =>
			(
				edge: VisualEdge,
				opts: {
					/** Override vis-network id. */
					idOverride?: string;
					/** Override from/to node ids (e.g. namespaced ids). */
					fromOverride?: string;
					toOverride?: string;
				} = {},
			): VisEdge => {
				const { idOverride, fromOverride, toOverride } = opts;
				const isInferred = edge.inferred === true;
				const edgeLabel = edge.hideLabel ? "" : edge.label;
				const dashes = edge.dashes ?? isInferred;
				const from = fromOverride ?? (typeof edge.source === "string" ? edge.source : edge.source.id);
				const to = toOverride ?? (typeof edge.target === "string" ? edge.target : edge.target.id);

				return {
					id: idOverride ?? edge.id,
					from,
					to,
					label: edgeLabel,
					color: {
						color: isInferred ? graphTheme.textMuted : graphTheme.borderStrong,
						highlight: graphTheme.accentSoft,
						hover: graphTheme.accent,
						opacity: isInferred ? 0.5 : 0.8,
					},
					dashes,
					arrows: { to: { enabled: true, scaleFactor: 1.2, type: "arrow" as const } },
					font: {
						size: 12,
						face: "Inter, system-ui, sans-serif",
						color: graphTheme.textMuted,
						align: "middle" as const,
						strokeWidth: 2,
						strokeColor: graphTheme.surface,
					},
					width: isInferred ? 1.5 : 2.5,
					selectionWidth: 3,
					hoverWidth: 3,
					smooth: { enabled: true, type: "dynamic" as const, roundness: 0.5 },
				};
			},
		[graphTheme],
	);

	const networkOptions = useMemo(
		() => ({
			physics: {
				enabled: true,
				solver: "forceAtlas2Based" as const,
				forceAtlas2Based: {
					gravitationalConstant: -70,
					centralGravity: 0.02,
					springLength: 120,
					springConstant: 0.07,
					damping: 0.7,
					avoidOverlap: 0.9,
				},
				minVelocity: 1,
				adaptiveTimestep: true,
				stabilization: { iterations: 280, enabled: true, fit: true, updateInterval: 50 },
			},
			edges: {
				color: { inherit: false },
				font: { size: 12, face: "Inter, system-ui, sans-serif", align: "middle" as const },
				smooth: { enabled: true, type: "continuous" as const, roundness: 0.5 },
				shadow: { enabled: theme === "dark", color: "rgba(0,0,0,0.5)", size: 5, x: 1, y: 1 },
			},
			nodes: {
				font: {
					size: 13,
					face: "Inter, system-ui, -apple-system, sans-serif",
					align: "center" as const,
				},
				scaling: {
					min: 16,
					max: 40,
					label: { enabled: true, min: 11, max: 15, drawThreshold: 5, maxVisible: 20 },
				},
				shadow: { enabled: true },
				margin: { top: 10, right: 10, bottom: 10, left: 10 },
			},
			interaction: {
				hover: true,
				hoverConnectedEdges: true,
				tooltipDelay: 150,
				navigationButtons: false,
				keyboard: { enabled: true, bindToWindow: false, speed: { x: 0.8, y: 0.8, zoom: 0.02 } },
				zoomView: true,
				dragView: true,
				zoomSpeed: 0.3,
			},
			layout: {
				improvedLayout: true,
				hierarchical: { enabled: false },
			},
		}),
		[theme],
	);

	return { buildVisNode, buildVisEdge, networkOptions, theme, graphTheme };
}
